import { useState } from 'react';
import { Auth } from 'aws-amplify';
import { toast } from 'react-toastify';
import { sanitizeFileName } from '../../utils/helpers/operationHelpers';
import { ExtractionPayload, ExtractionResponse, FileEntry, FileStatus, MainRiskSource } from '../../types/draweeRisk';
import appConfig from '../../../../../config/app-config';

const { address: API_URL, key: API_KEY } = appConfig.apis.contractsManagementApiEnv;
const { address: PERSON_SERVICE_URL } = appConfig.apis.personApiEnv;
const { address: GEOLOCATION_API_URL, key: GEOLOCATION_API_KEY } = appConfig.apis.geolocationApiEnv;

const EXTRACTOR_URL = API_URL.replace(/\/contracts-management\/?$/, '/contract-extractor');

// ============================================
// HELPERS
// ============================================

const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

const generateId = (): string => crypto.randomUUID();

type MainRiskCandidate = { uuid: string | null; name: string; document: string };

const resolveMainRiskCandidate = (
    source: MainRiskSource,
    candidates: {
        sacado: MainRiskCandidate;
        cedente: MainRiskCandidate;
        outro?: MainRiskCandidate;
    }
): MainRiskCandidate | null => {
    if (source === MainRiskSource.CEDENTE) return candidates.cedente;
    if (source === MainRiskSource.OUTRO) return candidates.outro ?? null;
    return candidates.sacado;
};

// ============================================
// PERSON RESOLUTION
// ============================================

const findPersonByDocument = async (document: string, token: string): Promise<string | null> => {
    const cleanDoc = document.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
    if (!cleanDoc) return null;

    const response = await fetch(`${PERSON_SERVICE_URL}/person/ALL/${cleanDoc}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY || '' },
    });

    if (!response.ok) return null;
    const data = await response.json();
    if (!data.results || data.results.length === 0) return null;

    const match = data.results.find((person: any) => {
        if (!person.documents || person.documents.length === 0) return false;
        return person.documents.some((doc: any) => {
            const personDoc = (doc.number || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
            return personDoc === cleanDoc;
        });
    });

    return match?.uuid || null;
};

const resolveParticipantPersonIds = async (
    participants: ExtractionPayload['participants'],
    token: string
): Promise<{ resolved: ExtractionPayload['participants']; unresolved: string[] }> => {
    const unresolved: string[] = [];

    const resolved = await Promise.all(
        participants.map(async (p) => {
            if (p.person_id) return p;
            if (!p.document) {
                unresolved.push(p.name || p.participant_type);
                return p;
            }
            const personId = await findPersonByDocument(p.document, token);
            if (!personId) {
                unresolved.push(`${p.name} (${p.document})`);
                return p;
            }
            return { ...p, person_id: personId };
        })
    );

    return { resolved, unresolved };
};

// ============================================
// BILLING ADDRESS
// ============================================

const fetchBillingAddress = async (personId: string, name: string, document: string, token: string) => {
    try {
        const res = await fetch(`${PERSON_SERVICE_URL}/person/${personId}/addresses`, {
            headers: { 'x-api-key': API_KEY || '', 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return null;
        const data = await res.json();
        const addresses = data.addresses || data || [];
        if (addresses.length === 0) return null;
        const addr = addresses[0];

        let cityName = addr.city || '';
        let stateName = addr.state || addr.province || '';

        if (addr.cityId && (!cityName || !stateName)) {
            try {
                const cityRes = await fetch(`${GEOLOCATION_API_URL}/cities/${addr.cityId}?fields=id,name,province`, {
                    headers: { 'x-api-key': GEOLOCATION_API_KEY || '' },
                });
                if (cityRes.ok) {
                    const cityData = await cityRes.json();
                    if (!cityName) cityName = cityData.name || '';
                    if (!stateName) stateName = cityData.province?.initials || cityData.province || '';
                }
            } catch { /* ignore */ }
        }

        return {
            name,
            document_number: document,
            address_street: addr.street || '',
            address_number: addr.number || '',
            address_complement: addr.complement || '',
            address_neighborhood: addr.district || addr.neighborhood || '',
            address_city: cityName,
            address_state: stateName,
            address_zip_code: addr.zipcode || addr.zip_code || '',
        };
    } catch { return null; }
};

// ============================================
// ENRICHMENT (resolução de pessoas, endereço e risco principal)
// ============================================

const enrichExtraction = async (
    data: ExtractionResponse,
    token: string
): Promise<{ data: ExtractionResponse; unresolved: string[] }> => {
    // Resolve person_id for participants
    const { resolved, unresolved } = await resolveParticipantPersonIds(data.payload.participants, token);
    data.payload.participants = resolved;

    // Resolve person_id for unique sacados in underlying_receivables
    const receivables = data.payload.operation_dictionary.underlying_receivables;
    let sacadoPersonId: string | null = null;
    let sacadoName = '';
    let sacadoDocument = '';

    if (receivables?.items?.length > 0) {
        const uniqueDocs = Array.from(new Set(receivables.items.map(item => item.cnpj_sacado).filter(Boolean)));
        const docToPersonId: Record<string, string | null> = {};

        await Promise.all(
            uniqueDocs.map(async (doc) => {
                docToPersonId[doc] = await findPersonByDocument(doc, token);
            })
        );

        receivables.items = receivables.items.map(item => ({
            ...item,
            person_id: item.cnpj_sacado ? (docToPersonId[item.cnpj_sacado] || null) : null,
        }));

        // Use the first sacado found for billing address
        const firstSacadoDoc = uniqueDocs[0];
        if (firstSacadoDoc) {
            sacadoPersonId = docToPersonId[firstSacadoDoc] || null;
            const firstItem = receivables.items.find(item => item.cnpj_sacado === firstSacadoDoc);
            sacadoName = firstItem?.sacado || '';
            sacadoDocument = firstSacadoDoc;
        }
    }

    // Fetch billing address from sacado (from underlying_receivables)
    if (sacadoPersonId) {
        const billingAddress = await fetchBillingAddress(sacadoPersonId, sacadoName, sacadoDocument, token);
        if (billingAddress) {
            data.payload.operation_dictionary.assignor_billing_address = billingAddress;
        } else {
            unresolved.push(`Endereço não encontrado para ${sacadoName} (${sacadoDocument})`);
        }
    } else if (receivables?.items?.length > 0) {
        const firstItem = receivables.items[0];
        unresolved.push(`Sacado não encontrado no cadastro: ${firstItem.sacado} (${firstItem.cnpj_sacado})`);
    }

    // Risco principal do Risco Sacado é o sacado/âncora por padrão.
    // O cedente (ASSIGNOR) é pré-resolvido como candidato alternativo.
    const assignor = resolved.find(p => p.participant_type === 'ASSIGNOR');
    const cedenteCandidate = {
        uuid: assignor?.person_id ?? null,
        name: assignor?.name ?? '',
        document: assignor?.document ?? '',
    };

    if (sacadoDocument) {
        const mainRiskUuid = sacadoPersonId ?? await findPersonByDocument(sacadoDocument, token);
        const sacadoCandidate = { uuid: mainRiskUuid, name: sacadoName, document: sacadoDocument };

        data.payload.operation_dictionary.main_risk = {
            ...sacadoCandidate,
            source: MainRiskSource.SACADO,
        };
        data.payload.operation_dictionary.main_risk_candidates = {
            sacado: sacadoCandidate,
            cedente: cedenteCandidate,
        };
        if (!mainRiskUuid) {
            unresolved.push(`Risco principal não encontrado no cadastro: ${sacadoName} (${sacadoDocument})`);
        }
    }

    return { data, unresolved };
};

// ============================================
// HOOK
// ============================================

export const useDraweeRiskExtraction = () => {
    const [files, setFiles] = useState<FileEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);

    const extractFromPdf = async (entry: FileEntry) => {
        try {
            const base64 = await fileToBase64(entry.file);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const response = await fetch(`${EXTRACTOR_URL}/extract/program-receivables`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-api-key': API_KEY || '',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ pdf: base64, mode: 'parser' }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                const body = errorData.body
                    ? (typeof errorData.body === 'string' ? JSON.parse(errorData.body) : errorData.body)
                    : errorData;

                if (response.status === 409 || body.duplicate) {
                    setFiles(prev => prev.map(f =>
                        f.id === entry.id
                            ? { ...f, status: FileStatus.ERROR, errorMessage: body.message || 'Arquivo já processado anteriormente', duplicateInfo: body.existing_operation }
                            : f
                    ));
                    return;
                }
                throw new Error(body.error || body.message || `Erro ${response.status}`);
            }

            const rawData = await response.json();
            const parsedBody = rawData.body
                ? (typeof rawData.body === 'string' ? JSON.parse(rawData.body) : rawData.body)
                : rawData;

            if (rawData.statusCode && rawData.statusCode >= 400) {
                if (rawData.statusCode === 409 || parsedBody.duplicate) {
                    setFiles(prev => prev.map(f =>
                        f.id === entry.id
                            ? { ...f, status: FileStatus.ERROR, errorMessage: parsedBody.message || 'Arquivo já processado anteriormente', duplicateInfo: parsedBody.existing_operation }
                            : f
                    ));
                    return;
                }
                throw new Error(parsedBody.error || parsedBody.message || `Erro ${rawData.statusCode}`);
            }

            const data: ExtractionResponse = parsedBody;
            if (!data.valid) throw new Error('PDF não reconhecido como contrato de Risco Sacado válido');

            // Renderiza o card imediatamente com os dados do contrato extraído,
            // enquanto os dados complementares (pessoas, endereço, risco principal)
            // são resolvidos em seguida com indicação de loading no card.
            setFiles(prev => prev.map(f =>
                f.id === entry.id
                    ? { ...f, status: FileStatus.SUCCESS, response: data, pdfBase64: base64, isEnriching: true }
                    : f
            ));

            const { data: enriched, unresolved } = await enrichExtraction(data, token);

            setFiles(prev => prev.map(f =>
                f.id === entry.id
                    ? { ...f, status: FileStatus.SUCCESS, response: enriched, pdfBase64: base64, isEnriching: false, unresolvedPersons: unresolved.length > 0 ? unresolved : undefined }
                    : f
            ));
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao processar PDF';
            setFiles(prev => prev.map(f =>
                f.id === entry.id ? { ...f, status: FileStatus.ERROR, errorMessage: message } : f
            ));
        }
    };

    const handleFilesSelected = (selectedFiles: FileList) => {
        const pdfFiles = Array.from(selectedFiles).filter(f => f.type === 'application/pdf');

        if (pdfFiles.length === 0) {
            toast.error('Selecione apenas arquivos PDF');
            return;
        }

        const fileKey = (file: File) => `${file.name}-${file.size}`;
        // Pré-atribui um id estável por arquivo para que a atualização de estado
        // seja idempotente (evita duplicação em StrictMode / dupla invocação).
        const candidates = pdfFiles.map(file => ({ id: generateId(), file, key: fileKey(file) }));
        let acceptedEntries: FileEntry[] = [];
        let duplicates: string[] = [];

        // Deduplica usando o estado MAIS ATUAL (prev), evitando stale closure
        // quando o usuário seleciona o mesmo arquivo enquanto outro ainda processa.
        setFiles(prev => {
            const existingKeys = new Set(prev.map(entry => fileKey(entry.file)));
            const accepted: FileEntry[] = [];
            const dups: string[] = [];
            candidates.forEach(({ id, file, key }) => {
                if (existingKeys.has(key)) {
                    dups.push(file.name);
                    return;
                }
                existingKeys.add(key);
                accepted.push({ id, file, status: FileStatus.UPLOADING as FileStatus });
            });
            acceptedEntries = accepted;
            duplicates = dups;
            return accepted.length > 0 ? [...prev, ...accepted] : prev;
        });

        if (duplicates.length > 0) {
            toast.warning(`Arquivo(s) já adicionado(s) e ignorado(s): ${Array.from(new Set(duplicates)).join(', ')}`);
        }

        acceptedEntries.forEach(entry => extractFromPdf(entry));
    };

    const handleRemoveFile = (id: string) => {
        setFiles(prev => prev.filter(f => f.id !== id));
    };

    const handleChangeMainRisk = (
        id: string,
        source: MainRiskSource,
        person?: { uuid: string | null; name: string; document: string }
    ) => {
        setFiles(prev => prev.map(f => {
            if (f.id !== id || !f.response) return f;

            const dict = f.response.payload.operation_dictionary;
            const candidates = dict.main_risk_candidates;
            if (!candidates) return f;

            const nextCandidates = source === MainRiskSource.OUTRO && person
                ? { ...candidates, outro: person }
                : candidates;

            const selected = resolveMainRiskCandidate(source, nextCandidates);
            if (!selected) return f;

            return {
                ...f,
                response: {
                    ...f.response,
                    payload: {
                        ...f.response.payload,
                        operation_dictionary: {
                            ...dict,
                            main_risk: { ...selected, source },
                            main_risk_candidates: nextCandidates,
                        },
                    },
                },
            };
        }));
    };

    const handleSave = async (navigate: (path: string) => void) => {
        const successFiles = files.filter(f => f.status === FileStatus.SUCCESS && f.response && !f.isEnriching);
        if (successFiles.length === 0) {
            toast.error('Nenhum contrato processado com sucesso para salvar');
            return;
        }

        const filesWithUnresolved = successFiles.filter(f => f.unresolvedPersons && f.unresolvedPersons.length > 0);
        if (filesWithUnresolved.length > 0) {
            toast.error(`${filesWithUnresolved.length} contrato(s) com participantes não encontrados no cadastro.`);
            return;
        }

        try {
            setIsLoading(true);
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();

            const results = await Promise.allSettled(
                successFiles.map(async (entry) => {
                    const payload = { ...entry.response!.payload };
                    const contractFileName = sanitizeFileName(entry.file.name);

                    payload.operation_number = payload.operation_dictionary?.operation_number || payload.operation_number || '';
                    // Remove campo auxiliar de UI (candidatos de risco principal) antes de salvar.
                    const { main_risk_candidates: _candidates, ...dictWithoutCandidates } = payload.operation_dictionary;
                    payload.operation_dictionary = {
                        ...dictWithoutCandidates,
                        file_hash: entry.response!.file_hash || '',
                        files: { ...payload.operation_dictionary.files, contract_file: contractFileName },
                    };

                    const response = await fetch(`${API_URL}/contracts/receivables_assignment/save`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY || '', 'Content-Type': 'application/json' },
                        body: JSON.stringify(payload),
                    });

                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        throw new Error(errorData.message || `Erro ao salvar ${entry.file.name}`);
                    }

                    const data = await response.json();

                    // Upload PDF via separate endpoint
                    const creditOperationId = data.credit_operation_id || data.id;
                    if (creditOperationId && entry.pdfBase64) {
                        const uploadResponse = await fetch(`${API_URL}/contracts/${creditOperationId}/upload-file`, {
                            method: 'POST',
                            headers: { 'Authorization': `Bearer ${token}`, 'x-api-key': API_KEY || '', 'Content-Type': 'application/json' },
                            body: JSON.stringify({ file_name: contractFileName, file_content: entry.pdfBase64 }),
                        });
                        if (!uploadResponse.ok) throw new Error(`Falha no upload do arquivo ${entry.file.name}`);
                    }

                    return data;
                })
            );

            const fulfilled = results.filter(r => r.status === 'fulfilled').length;
            const rejected = results.filter(r => r.status === 'rejected').length;

            if (fulfilled > 0) toast.success(`${fulfilled} operação(ões) salva(s) com sucesso!`);
            if (rejected > 0) toast.error(`${rejected} operação(ões) com erro ao salvar`);
            if (rejected === 0) navigate('/contracts/0/100/id/ASC/s=;');
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Erro ao conectar com o servidor';
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    };

    const processingCount = files.filter(f => f.status === FileStatus.UPLOADING || f.isEnriching).length;
    const readyToSaveCount = files.filter(f => f.status === FileStatus.SUCCESS && !f.isEnriching && !f.unresolvedPersons?.length).length;

    return { files, isLoading, processingCount, readyToSaveCount, handleFilesSelected, handleRemoveFile, handleChangeMainRisk, handleSave };
};
